<template>
  <div class="sub-page">

    <!-- Header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">A minha Subscrição</div>
          <div class="page-subtitle">Estado do plano e gestão dos seus anúncios</div>
        </div>
        <button class="refresh-btn-header" @click="loadAll" :disabled="loading">
          <q-icon name="refresh" size="18px" :class="loading ? 'spin' : ''" />
        </button>
      </div>
    </div>

    <div class="content-wrapper">

      <!-- Loading state -->
      <div v-if="loading" class="q-mt-md">
        <q-skeleton type="rect" height="140px" class="q-mb-md" style="border-radius:16px" />
        <q-skeleton type="rect" height="60px" class="q-mb-md" style="border-radius:12px" />
        <div v-for="n in 4" :key="n" class="skeleton-row">
          <q-skeleton type="rect" width="100%" height="56px" style="border-radius:10px" />
        </div>
      </div>

      <template v-else>

        <div v-if="queuedSubscriptions.length" class="queued-sub-card">
          <div class="queued-sub-heading">
            <q-icon name="schedule" size="20px" />
            <strong>Plano em espera</strong>
          </div>
          <div v-for="queued in queuedSubscriptions" :key="queued.id" class="queued-sub-item">
            <div>
              <strong>{{ queued.plan_name }}</strong>
              <span>O pagamento foi confirmado. Este plano será activado quando terminar a subscrição actual.</span>
            </div>
            <small>Início previsto: {{ formatDate(queued.starts_at) }}</small>
          </div>
        </div>

        <!-- Subscription card -->
        <div v-if="subscription" class="sub-card">
          <div class="sub-card-top">
            <div>
              <div class="sub-plan-name">{{ subscription.plan?.name || '—' }}</div>
              <div class="sub-meta-row">
                <span class="status-badge" :class="`status-badge--${subscription.status}`">
                  {{ statusLabel(subscription.status) }}
                </span>
                <span class="sub-meta-item" v-if="subscription.ends_at">
                  <q-icon name="event" size="13px" class="q-mr-xs" />
                  Válido até {{ formatDate(subscription.ends_at) }}
                </span>
                <span class="sub-meta-item" v-if="subscription.days_remaining != null">
                  <q-icon name="hourglass_bottom" size="13px" class="q-mr-xs" />
                  {{ subscription.days_remaining }} dias restantes
                </span>
              </div>
            </div>
            <div class="sub-slots">
              <div class="slots-used">{{ subscription.used_slots }}</div>
              <div class="slots-sep">/</div>
              <div class="slots-total">{{ subscription.quota }}</div>
              <div class="slots-label">slots</div>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="progress-wrap">
            <div class="progress-label">
              <span>Imóveis activos</span>
              <span>{{ subscription.used_slots }} de {{ subscription.quota }}</span>
            </div>
            <q-linear-progress
              :value="subscription.quota > 0 ? subscription.used_slots / subscription.quota : 0"
              :color="progressColor"
              track-color="grey-3"
              rounded
              size="10px"
            />
            <div class="progress-note" v-if="subscription.available_slots === 0">
              <q-icon name="warning" size="13px" class="q-mr-xs" color="warning" />
              Quota esgotada. Contacte o suporte para fazer upgrade.
            </div>
          </div>

          <!-- Plan features -->
          <div class="sub-features" v-if="subscription.plan">
            <div class="feature-chip" v-if="subscription.plan.max_photos">
              <q-icon name="photo_library" size="14px" class="q-mr-xs" />
              Até {{ subscription.plan.max_photos }} fotos
            </div>
            <div class="feature-chip" v-if="subscription.plan.is_featured_home">
              <q-icon name="star" size="14px" class="q-mr-xs" />
              Destaque na home
            </div>
            <div class="feature-chip" v-if="subscription.plan.shows_above_normal">
              <q-icon name="arrow_upward" size="14px" class="q-mr-xs" />
              Prioridade na listagem
            </div>
            <div class="feature-chip" v-if="subscription.plan.whatsapp_button">
              <q-icon name="chat" size="14px" class="q-mr-xs" />
              Botão WhatsApp
            </div>
          </div>

          <!-- Renew / upgrade button -->
          <div class="sub-card-footer">
            <router-link to="/planos" class="mpesa-btn">
              <q-icon name="workspace_premium" size="16px" class="q-mr-xs" />
              {{ subscription.status === 'active' ? 'Renovar / Actualizar plano' : 'Ver planos disponíveis' }}
            </router-link>
          </div>
        </div>

        <!-- No subscription CTA -->
        <div v-else class="no-sub-card">
          <q-icon name="workspace_premium" size="56px" color="grey-4" />
          <div class="no-sub-title">Sem subscrição activa</div>
          <div class="no-sub-text">
            Para publicar anúncios precisa de uma subscrição activa.
          </div>
          <div class="no-sub-actions">
            <router-link to="/planos" class="mpesa-btn">
              <q-icon name="workspace_premium" size="16px" class="q-mr-xs" />
              Ver planos e subscrever
            </router-link>
          </div>
        </div>

        <!-- Properties section -->
        <div class="section-title q-mt-lg">
          <q-icon name="home_work" size="18px" class="q-mr-xs" />
          Os meus imóveis
          <span class="section-count" v-if="properties.length">{{ properties.length }}</span>
        </div>

        <!-- Error alert -->
        <div v-if="activationError" class="error-alert">
          <q-icon name="error" size="16px" class="q-mr-xs" />
          {{ activationError }}
          <button class="error-dismiss" @click="activationError = null">✕</button>
        </div>

        <!-- Empty properties -->
        <div v-if="properties.length === 0" class="empty-state">
          <q-icon name="home_work" size="48px" color="grey-4" />
          <div class="empty-text">Nenhum imóvel encontrado</div>
        </div>

        <!-- Properties list -->
        <div v-else class="prop-list">
          <div v-for="prop in properties" :key="prop.id" class="prop-row">
            <!-- Thumbnail -->
            <div class="prop-thumb">
              <img
                v-if="prop.images && prop.images.length"
                :src="prop.images[0]"
                alt=""
              />
              <q-icon v-else name="image_not_supported" size="22px" color="grey-4" />
            </div>

            <!-- Info -->
            <div class="prop-info">
              <div class="prop-title">{{ prop.title }}</div>
              <div class="prop-slug text-grey-5 text-caption">{{ prop.slug }}</div>
            </div>

            <!-- Status badge -->
            <div class="prop-status">
              <span class="status-badge" :class="`status-badge--${prop.status}`">
                {{ propStatusLabel(prop.status) }}
              </span>
              <div v-if="prop.status === 'expired'" class="text-caption text-grey-5 q-mt-xs">
                Subscrição expirada — imóvel não visível ao público
              </div>
              <div v-if="prop.rejection_reason" class="text-caption text-negative q-mt-xs">
                {{ prop.rejection_reason }}
              </div>
            </div>

            <!-- Actions -->
            <div class="prop-actions">
              <button
                v-if="!['active', 'pending_review'].includes(prop.status)"
                class="action-btn action-btn--activate"
                :disabled="!canActivate || actionLoading === prop.id"
                @click="handleActivate(prop)"
              >
                <q-icon name="publish" size="16px" class="q-mr-xs" />
                <span v-if="actionLoading === prop.id">...</span>
                <span v-else>Enviar para validação</span>
                <q-tooltip v-if="!canActivate">Quota esgotada — faça upgrade do seu plano</q-tooltip>
              </button>
              <button
                v-if="prop.status === 'active'"
                class="action-btn action-btn--deactivate"
                :disabled="actionLoading === prop.id"
                @click="confirmDeactivate(prop)"
              >
                <q-icon name="toggle_off" size="16px" class="q-mr-xs" />
                <span v-if="actionLoading === prop.id">...</span>
                <span v-else>Desactivar</span>
              </button>
            </div>
          </div>
        </div>

        <div class="section-title q-mt-lg">
          <q-icon name="hotel" size="18px" class="q-mr-xs" />
          Os meus alojamentos
          <span class="section-count" v-if="accommodations.length">{{ accommodations.length }}</span>
        </div>
        <div v-if="!accommodations.length" class="empty-state">
          <q-icon name="hotel" size="42px" color="grey-4" />
          <div class="empty-text">Nenhum alojamento encontrado</div>
        </div>
        <div v-else class="prop-list">
          <div v-for="item in accommodations" :key="item.id" class="prop-row">
            <div class="prop-thumb">
              <img v-if="getListingImage(item)" :src="getListingImage(item)" alt="" />
              <q-icon v-else name="image_not_supported" size="22px" color="grey-4" />
            </div>
            <div class="prop-info">
              <div class="prop-title">{{ item.title }}</div>
              <div class="prop-slug text-grey-5 text-caption">{{ item.location }}</div>
            </div>
            <div class="prop-status">
              <span class="status-badge" :class="`status-badge--${item.status}`">
                {{ listingStatusLabel(item.status) }}
              </span>
              <div v-if="item.rejection_reason" class="text-caption text-negative q-mt-xs">
                {{ item.rejection_reason }}
              </div>
            </div>
            <div class="prop-actions">
              <button class="action-btn" @click="viewListing('accommodations', item)">
                <q-icon name="open_in_new" size="16px" class="q-mr-xs" />Detalhes
              </button>
              <button
                v-if="['draft', 'inactive', 'rejected'].includes(item.status)"
                class="action-btn action-btn--activate"
                :disabled="actionLoading === `accommodation-${item.id}`"
                @click="handleListingSubmit('accommodations', item)"
              >
                <q-icon name="publish" size="16px" class="q-mr-xs" />
                <span v-if="actionLoading === `accommodation-${item.id}`">...</span>
                <span v-else>Enviar para validação</span>
              </button>
              <button
                v-else-if="item.status === 'published'"
                class="action-btn action-btn--deactivate"
                :disabled="actionLoading === `accommodation-${item.id}`"
                @click="handleListingDeactivate('accommodations', item)"
              >
                <q-icon name="toggle_off" size="16px" class="q-mr-xs" />Desactivar
              </button>
            </div>
          </div>
        </div>

        <div class="section-title q-mt-lg">
          <q-icon name="directions_car" size="18px" class="q-mr-xs" />
          As minhas viaturas rent-a-car
          <span class="section-count" v-if="rentalVehicles.length">{{ rentalVehicles.length }}</span>
        </div>
        <div v-if="!rentalVehicles.length" class="empty-state">
          <q-icon name="directions_car" size="42px" color="grey-4" />
          <div class="empty-text">Nenhuma viatura encontrada</div>
        </div>
        <div v-else class="prop-list">
          <div v-for="item in rentalVehicles" :key="item.id" class="prop-row">
            <div class="prop-thumb">
              <img v-if="getListingImage(item)" :src="getListingImage(item)" alt="" />
              <q-icon v-else name="image_not_supported" size="22px" color="grey-4" />
            </div>
            <div class="prop-info">
              <div class="prop-title">{{ item.title }}</div>
              <div class="prop-slug text-grey-5 text-caption">{{ item.location }}</div>
            </div>
            <div class="prop-status">
              <span class="status-badge" :class="`status-badge--${item.status}`">
                {{ listingStatusLabel(item.status) }}
              </span>
              <div v-if="item.rejection_reason" class="text-caption text-negative q-mt-xs">
                {{ item.rejection_reason }}
              </div>
            </div>
            <div class="prop-actions">
              <button class="action-btn" @click="viewListing('rentalVehicles', item)">
                <q-icon name="open_in_new" size="16px" class="q-mr-xs" />Detalhes
              </button>
              <button
                v-if="['draft', 'inactive', 'rejected'].includes(item.status)"
                class="action-btn action-btn--activate"
                :disabled="actionLoading === `rental_vehicle-${item.id}`"
                @click="handleListingSubmit('rentalVehicles', item)"
              >
                <q-icon name="publish" size="16px" class="q-mr-xs" />
                <span v-if="actionLoading === `rental_vehicle-${item.id}`">...</span>
                <span v-else>Enviar para validação</span>
              </button>
              <button
                v-else-if="item.status === 'published'"
                class="action-btn action-btn--deactivate"
                :disabled="actionLoading === `rental_vehicle-${item.id}`"
                @click="handleListingDeactivate('rentalVehicles', item)"
              >
                <q-icon name="toggle_off" size="16px" class="q-mr-xs" />Desactivar
              </button>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="meta && meta.last_page > 1" class="pagination-row">
          <q-pagination
            v-model="currentPage"
            :max="meta.last_page"
            :max-pages="6"
            direction-links
            boundary-links
            color="primary"
            active-design="unelevated"
            active-color="primary"
            active-text-color="white"
            @update:model-value="loadProperties"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import store from '@/vuex/store';
import router from '@/router';
import Swal from 'sweetalert2';

export default {
  name: 'MySubscription',

  setup() {
    const loading = ref(false);
    const actionLoading = ref(null);
    const activationError = ref(null);
    const currentPage = ref(1);
    const accommodations = ref([]);
    const rentalVehicles = ref([]);

    const subscription = computed(() => store.state.subscriptions.mySubscription);
    const queuedSubscriptions = computed(() => store.state.subscriptions.queuedSubscriptions);
    const properties = computed(() => store.state.subscriptions.myProperties);
    const meta = computed(() => store.state.subscriptions.myPropertiesMeta);

    const canActivate = computed(() => {
      if (!subscription.value) return false;
      if (subscription.value.status !== 'active') return false;
      return (subscription.value.available_slots ?? 0) > 0;
    });

    const progressColor = computed(() => {
      if (!subscription.value) return 'grey';
      const ratio = subscription.value.used_slots / subscription.value.quota;
      if (ratio >= 1) return 'negative';
      if (ratio >= 0.8) return 'warning';
      return 'positive';
    });

    const statusLabel = (s) => {
      const map = { active: 'Activa', expired: 'Expirada', pending: 'Pendente', cancelled: 'Cancelada', inactive: 'Inactiva' };
      return map[s] || s;
    };

    const propStatusLabel = (s) => {
      const map = { active: 'Activo', published: 'Publicado', inactive: 'Inactivo', draft: 'Rascunho', rejected: 'Requer correcção', expired: 'Expirado', pending_review: 'Em revisão' };
      return map[s] || s;
    };

    const listingStatusLabel = propStatusLabel;

    const getListingImage = (item) =>
      item.primary_image?.url ||
      item.primary_image_url ||
      item.images?.[0]?.url ||
      item.images?.[0] ||
      null;

    const formatDate = (d) => {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const loadSubscription = async () => {
      try {
        await store.dispatch('subscriptions/getMySubscription');
      } catch {
        // no active subscription is fine — state stays null
      }
    };

    const loadProperties = async (page = currentPage.value) => {
      try {
        await store.dispatch('subscriptions/getMyProperties', page);
      } catch (err) {
        console.error('Error loading properties:', err);
      }
    };

    const loadCatalogListings = async () => {
      try {
        const loadAllPages = async (moduleName) => {
          const items = [];
          let page = 1;
          let lastPage = 1;

          do {
            const result = await store.dispatch(`${moduleName}/list`, {
              page,
              per_page: 48,
            });
            items.push(...(result?.data || []));
            lastPage = Number(result?.last_page || 1);
            page += 1;
          } while (page <= lastPage);

          return items;
        };

        [accommodations.value, rentalVehicles.value] = await Promise.all([
          loadAllPages('accommodations'),
          loadAllPages('rentalVehicles'),
        ]);
      } catch (err) {
        console.error('Error loading accommodation and rental listings:', err);
        activationError.value = 'Não foi possível carregar os alojamentos e viaturas.';
      }
    };

    const loadAll = async () => {
      loading.value = true;
      activationError.value = null;
      try {
        await Promise.all([loadSubscription(), loadProperties(1), loadCatalogListings()]);
      } finally {
        loading.value = false;
      }
    };

    const handleActivate = async (prop) => {
      activationError.value = null;
      actionLoading.value = prop.id;
      try {
        await store.dispatch('subscriptions/activateProperty', prop.id);
        await store.dispatch('subscriptions/getMySubscription');
        Swal.fire({ title: 'Enviado!', text: `"${prop.title}" aguarda validação de um administrador.`, icon: 'success', timer: 2400, showConfirmButton: false });
      } catch (err) {
        const data = err.response?.data;
        const reason = data?.reason;
        if (reason === 'sem_subscricao') {
          activationError.value = 'Não tem subscrição activa. Contacte o suporte.';
        } else if (reason === 'quota_esgotada') {
          activationError.value = `Quota esgotada: ${data.usados} de ${data.quota} slots usados no plano "${data.plano}". Faça upgrade para continuar.`;
        } else if (reason === 'fotos_excedidas') {
          activationError.value = `O seu pacote permite até ${data.max_fotos} fotografias. Remova ${data.fotos - data.max_fotos} foto(s) antes de enviar.`;
        } else {
          activationError.value = data?.message || 'Erro ao enviar o imóvel para validação. Tente novamente.';
        }
      } finally {
        actionLoading.value = null;
      }
    };

    const handleListingSubmit = async (moduleName, item) => {
      const type = moduleName === 'accommodations' ? 'accommodation' : 'rental_vehicle';
      const loadingKey = `${type}-${item.id}`;
      activationError.value = null;
      actionLoading.value = loadingKey;
      try {
        await store.dispatch(`${moduleName}/publish`, item.id);
        await Promise.all([loadCatalogListings(), loadSubscription()]);
        Swal.fire({
          title: 'Enviado!',
          text: `"${item.title}" aguarda validação de um administrador.`,
          icon: 'success',
          timer: 2400,
          showConfirmButton: false,
        });
      } catch (err) {
        activationError.value =
          err.response?.data?.message ||
          'Não foi possível enviar o anúncio para validação. Verifique os dados e tente novamente.';
      } finally {
        actionLoading.value = null;
      }
    };

    const viewListing = (moduleName, item) => {
      const base = moduleName === 'accommodations' ? '/alojamentos' : '/rent-a-car';
      router.push(`${base}/${item.slug}`);
    };

    const handleListingDeactivate = async (moduleName, item) => {
      const type = moduleName === 'accommodations' ? 'accommodation' : 'rental_vehicle';
      const loadingKey = `${type}-${item.id}`;
      const result = await Swal.fire({
        title: 'Desactivar anúncio?',
        text: `"${item.title}" ficará oculto na plataforma.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Desactivar',
        cancelButtonText: 'Cancelar',
      });
      if (!result.isConfirmed) return;
      actionLoading.value = loadingKey;
      try {
        await store.dispatch(`${moduleName}/deactivate`, item.id);
        await loadCatalogListings();
        Swal.fire({ title: 'Anúncio desactivado', icon: 'success', timer: 1800, showConfirmButton: false });
      } catch (err) {
        activationError.value = err.response?.data?.message || 'Não foi possível desactivar o anúncio.';
      } finally {
        actionLoading.value = null;
      }
    };

    const confirmDeactivate = (prop) => {
      Swal.fire({
        title: 'Desactivar imóvel?',
        text: `"${prop.title}" ficará oculto na plataforma.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sim, desactivar',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        actionLoading.value = prop.id;
        try {
          await store.dispatch('subscriptions/deactivateProperty', prop.id);
          await store.dispatch('subscriptions/getMySubscription');
          Swal.fire({ title: 'Desactivado!', text: `"${prop.title}" foi desactivado.`, icon: 'success', timer: 2000, showConfirmButton: false });
        } catch {
          Swal.fire({ title: 'Erro', text: 'Não foi possível desactivar o imóvel. Tente novamente.', icon: 'error' });
        } finally {
          actionLoading.value = null;
        }
      });
    };

    onMounted(loadAll);

    return {
      loading, actionLoading, activationError, currentPage,
      subscription, queuedSubscriptions, properties, meta,
      accommodations, rentalVehicles,
      canActivate, progressColor,
      statusLabel, propStatusLabel, listingStatusLabel, getListingImage, formatDate,
      loadAll, loadProperties, handleActivate, handleListingSubmit, handleListingDeactivate, viewListing, confirmDeactivate,
    };
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.sub-page { background: #f4f6fb; min-height: 100vh; }

/* ── Header ───────────────────────────────────────────── */
.page-header { background: #0d1b2a; padding: 20px 24px; }
.page-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 900px; margin: 0 auto;
}
.page-title { font-size: 20px; font-weight: 700; color: #fff; }
.page-subtitle { font-size: 12px; color: rgba(212,175,55,.75); margin-top: 2px; letter-spacing: .4px; }
.refresh-btn-header {
  background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.15);
  border-radius: 10px; width: 38px; height: 38px; color: #fff;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Content ──────────────────────────────────────────── */
.content-wrapper { max-width: 900px; margin: 28px auto; padding: 0 16px 60px; }

/* ── Subscription card ────────────────────────────────── */
.sub-card {
  background: #0d1b2a; border-radius: 18px; padding: 24px;
  border: 1px solid rgba(212,175,55,.2); margin-bottom: 20px;
}
.sub-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.sub-plan-name { font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 8px; }
.sub-meta-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.sub-meta-item { display: flex; align-items: center; font-size: 12px; color: rgba(255,255,255,.55); }

.sub-slots {
  display: flex; align-items: baseline; gap: 4px;
  background: rgba(212,175,55,.1); border: 1px solid rgba(212,175,55,.25);
  border-radius: 14px; padding: 10px 18px; flex-shrink: 0;
}
.slots-used { font-size: 28px; font-weight: 800; color: #d4af37; }
.slots-sep { font-size: 18px; color: rgba(212,175,55,.5); }
.slots-total { font-size: 20px; font-weight: 600; color: rgba(212,175,55,.7); }
.slots-label { font-size: 11px; color: rgba(212,175,55,.5); margin-left: 4px; align-self: center; }

.progress-wrap { margin-bottom: 16px; }
.progress-label { display: flex; justify-content: space-between; font-size: 12px; color: rgba(255,255,255,.55); margin-bottom: 6px; }
.progress-note { display: flex; align-items: center; font-size: 11px; color: #f59e0b; margin-top: 6px; }

.sub-features { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.queued-sub-card {
  background: #fff; border: 1px solid #f0d98a; border-left: 4px solid #d4af37;
  border-radius: 14px; padding: 16px 18px; margin: 16px 0;
}
.queued-sub-heading { display: flex; align-items: center; gap: 8px; color: #8a6a13; margin-bottom: 12px; }
.queued-sub-item { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 10px 0; border-top: 1px solid #f1f3f6; }
.queued-sub-item div { display: grid; gap: 4px; }
.queued-sub-item strong { color: #172033; font-size: 13px; }
.queued-sub-item span, .queued-sub-item small { color: #64748b; font-size: 12px; }
@media (max-width: 600px) {
  .queued-sub-item { align-items: flex-start; flex-direction: column; gap: 6px; }
}
.feature-chip {
  display: flex; align-items: center; font-size: 11.5px; font-weight: 500;
  background: rgba(255,255,255,.07); color: rgba(255,255,255,.65);
  border: 1px solid rgba(255,255,255,.1); border-radius: 20px; padding: 4px 12px;
}

/* ── No subscription ──────────────────────────────────── */
.no-sub-card {
  background: #fff; border: 2px dashed #e2e8f0; border-radius: 18px;
  padding: 48px 24px; text-align: center; margin-bottom: 20px;
}
.no-sub-title { font-size: 18px; font-weight: 700; color: #0f172a; margin: 16px 0 8px; }
.no-sub-text { font-size: 14px; color: #64748b; max-width: 400px; margin: 0 auto 20px; line-height: 1.6; }
.cta-link {
  display: inline-block; background: #0d1b2a; color: #d4af37;
  padding: 10px 24px; border-radius: 10px; font-weight: 600;
  font-size: 13px; text-decoration: none; letter-spacing: .3px;
}

/* ── Section title ────────────────────────────────────── */
.section-title {
  display: flex; align-items: center; font-size: 15px; font-weight: 700;
  color: #0f172a; margin-bottom: 16px;
}
.section-count {
  margin-left: 8px; background: #e2e8f0; color: #64748b;
  font-size: 12px; font-weight: 600; border-radius: 20px;
  padding: 1px 8px;
}

/* ── Error alert ──────────────────────────────────────── */
.error-alert {
  display: flex; align-items: center; background: #fef2f2;
  border: 1px solid #fecaca; border-radius: 10px;
  padding: 12px 16px; font-size: 13px; color: #dc2626;
  margin-bottom: 16px; gap: 4px;
}
.error-dismiss { background: none; border: none; cursor: pointer; color: #dc2626; margin-left: auto; font-size: 14px; }

/* ── Empty ────────────────────────────────────────────── */
.empty-state {
  text-align: center; padding: 48px 16px;
  background: #fff; border-radius: 14px; border: 1px solid #e8ecf4;
}
.empty-text { font-size: 14px; color: #94a3b8; margin-top: 10px; }

/* ── Properties list ──────────────────────────────────── */
.prop-list { display: flex; flex-direction: column; gap: 8px; }
.prop-row {
  display: flex; align-items: center; gap: 14px;
  background: #fff; border-radius: 12px; padding: 14px 16px;
  border: 1px solid #e8ecf4;
}

.prop-thumb {
  width: 52px; height: 46px; border-radius: 8px;
  background: #f1f5f9; overflow: hidden; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.prop-thumb img { width: 100%; height: 100%; object-fit: cover; }

.prop-info { flex: 1; min-width: 0; }
.prop-title { font-size: 14px; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.prop-status { flex-shrink: 0; text-align: right; min-width: 90px; }

.prop-actions { display: flex; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; gap: 6px; }

.action-btn {
  display: flex; align-items: center; padding: 7px 14px;
  border-radius: 8px; font-size: 12.5px; font-weight: 600;
  cursor: pointer; border: 1px solid transparent;
  transition: background .15s;
}
.action-btn:disabled { opacity: .45; cursor: not-allowed; }
.action-btn--activate {
  background: rgba(34,197,94,.1); color: #16a34a;
  border-color: rgba(34,197,94,.25);
}
.action-btn--activate:not(:disabled):hover { background: rgba(34,197,94,.18); }
.action-btn--deactivate {
  background: rgba(239,68,68,.08); color: #dc2626;
  border-color: rgba(239,68,68,.2);
}
.action-btn--deactivate:not(:disabled):hover { background: rgba(239,68,68,.15); }

/* ── Status badges ────────────────────────────────────── */
.status-badge {
  display: inline-flex; align-items: center; padding: 3px 10px;
  border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: .3px;
}
.status-badge--active    { background: rgba(34,197,94,.12);  color: #16a34a; }
.status-badge--published { background: rgba(34,197,94,.12);  color: #16a34a; }
.status-badge--expired   { background: rgba(239,68,68,.1);   color: #dc2626; }
.status-badge--pending   { background: rgba(245,158,11,.12); color: #d97706; }
.status-badge--pending_review { background: rgba(59,130,246,.1); color: #2563eb; }
.status-badge--cancelled { background: rgba(107,114,128,.1); color: #6b7280; }
.status-badge--inactive  { background: rgba(148,163,184,.12);color: #94a3b8; }
.status-badge--draft     { background: rgba(148,163,184,.12);color: #94a3b8; }

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-row { margin-bottom: 8px; }

/* ── Pagination ───────────────────────────────────────── */
.pagination-row { display: flex; justify-content: center; margin-top: 24px; }

/* ── Sub card footer ──────────────────────────────────── */
.sub-card-footer { margin-top: 18px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,.08); }

/* ── No-sub actions ───────────────────────────────────── */
.no-sub-actions { display: flex; flex-direction: column; align-items: center; gap: 10px; margin-top: 4px; }
.cta-link-outline {
  display: inline-block; border: 1px solid rgba(13,27,42,.2); color: #475569;
  padding: 9px 22px; border-radius: 10px; font-size: 13px; font-weight: 600;
  text-decoration: none;
}
.cta-link-outline:hover { background: #f1f5f9; }

/* ── M-Pesa button ────────────────────────────────────── */
.mpesa-btn {
  display: inline-flex; align-items: center; padding: 10px 22px;
  background: #16a34a; color: #fff; border: none; border-radius: 10px;
  font-size: 13.5px; font-weight: 700; cursor: pointer; letter-spacing: .3px;
  transition: background .15s;
}
.mpesa-btn:hover { background: #15803d; }

</style>
